import { Modality } from '~/domain/dicom/modality';
import {
    PairEnd,
    pairEndLabels,
    SeriesNumberFormatter,
} from '../pairEndLabels';

/** One image's share of a blend, as the mix prints it beside its name. */
export interface PrintedBlendShare {
    /** Which end of the mix the share belongs to. */
    readonly end: 'row' | 'fused';
    /** The name the share is printed with. */
    readonly label: string;
    /** That image's share of the blend, 0..1. */
    readonly share: number;
}

/** The name a PET goes by in the mix, whichever end it is on. */
const PET_LABEL = 'PET';

/**
 * The share the mix of `rowImage` blended with `fused` prints, given the
 * row image's share `alpha` (0..1). A pair with exactly one PET prints the
 * PET's share, named PET, whichever end the PET is on, so the number reads
 * as the PET opacity; any other pair prints the fused image's share, named
 * as {@link pairEndLabels} names it with `nameSeries`.
 */
export function printedBlendShare(
    rowImage: PairEnd,
    fused: PairEnd,
    alpha: number,
    nameSeries: SeriesNumberFormatter
): PrintedBlendShare {
    const rowIsPet = rowImage.modality === Modality.PT;
    const fusedIsPet = fused.modality === Modality.PT;
    if (rowIsPet && !fusedIsPet) {
        return { end: 'row', label: PET_LABEL, share: alpha };
    }
    if (fusedIsPet && !rowIsPet) {
        return { end: 'fused', label: PET_LABEL, share: 1 - alpha };
    }
    return {
        end: 'fused',
        label: pairEndLabels(rowImage, fused, nameSeries)[1],
        share: 1 - alpha,
    };
}
